import { ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { UpdateAppService } from '@services/update-app.service';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@environments/environment';

@Component({
  selector: 'download-app-prompt',
  templateUrl: './download-app-prompt.component.html',
  styleUrls: ['./download-app-prompt.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule
  ],
})
export class DownloadAppPromptComponent {
  protected updateAppService = inject(UpdateAppService);
  private readonly fallbackHost: string = this.extractHost(environment.urlApp);

  protected swipeOffset: number = 0;
  protected isDragging: boolean = false;
  protected isDismissed: boolean = false;
  private readonly swipeDismissThreshold: number = 100;
  private readonly swipeHideAnimationMs: number = 200;
  private pointerActive: boolean = false;
  private startX: number = 0;
  private activePointerId: number | null = null;
  @ViewChild('promptCard', { static: false }) private promptCardRef?: ElementRef<HTMLElement>;

  protected get appDomain(): string {
    const host: string = (typeof window === 'undefined') ? this.fallbackHost : window.location.host;
    return host.startsWith('localhost') ? this.fallbackHost : host;
  }

  protected get cardTransform(): string {
    return `translateX(${this.swipeOffset}px)`;
  }

  protected onInstallClick(): void {
    this.updateAppService.hideDownloadPrompt();
    void this.updateAppService.downloadApp();
  }

  protected onPointerDown(event: PointerEvent): void {
    if (this.isDismissed) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof HTMLElement && event.target.closest('.action')) return;
    this.pointerActive = true;
    this.activePointerId = event.pointerId;
    this.startX = event.clientX;
    this.swipeOffset = 0;
    this.isDragging = true;
    this.promptCardRef?.nativeElement.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.pointerActive || event.pointerId !== this.activePointerId) return;
    this.swipeOffset = event.clientX - this.startX;
  }

  protected onPointerUp(event: PointerEvent): void {
    if (!this.pointerActive || event.pointerId !== this.activePointerId) return;
    this.promptCardRef?.nativeElement.releasePointerCapture?.(event.pointerId);
    const shouldDismiss: boolean = Math.abs(this.swipeOffset) > this.swipeDismissThreshold;
    shouldDismiss ? this.dismissWithSwipe() : this.resetSwipeState();
  }

  protected onPointerCancel(event: PointerEvent): void {
    if (!this.pointerActive || event.pointerId !== this.activePointerId) return;
    this.promptCardRef?.nativeElement.releasePointerCapture?.(event.pointerId);
    this.resetSwipeState();
  }

  private dismissWithSwipe(): void {
    const direction: number = this.swipeOffset >= 0 ? 1 : -1;
    this.pointerActive = false;
    this.activePointerId = null;
    this.isDragging = false;
    this.isDismissed = true;
    this.swipeOffset = direction * 600;

    setTimeout(() => {
      this.updateAppService.hideDownloadPrompt();
      this.resetSwipeState();
      this.isDismissed = false;
    }, this.swipeHideAnimationMs);
  }

  private resetSwipeState(): void {
    this.pointerActive = false;
    this.activePointerId = null;
    this.startX = 0;
    this.swipeOffset = 0;
    this.isDragging = false;
  }

  private extractHost(url: string): string {
    const normalizedUrl: string = url.trim();

    if (!normalizedUrl) {
      return 'localhost';
    }

    try {
      return new URL(normalizedUrl).host;
    } catch {
      return normalizedUrl
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .trim() || 'localhost';
    }
  }
}
